import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Box, Typography, Button, Container, Grid, Avatar, Rating } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import TrackedButton from '../components/TrackedButton';
import { useNavigate } from 'react-router-dom';

const StyledFeatureCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const StyledIcon = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
}));

const StyledStatCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '24px',
  padding: '2rem',
  textAlign: 'center',
  border: '1px solid rgba(255, 255, 255, 0.05)',
}));

const StyledTestimonialCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const StyledPricingCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Advertisers = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const navigate = useNavigate();

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 64px)',
      marginTop: 0,
      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
      color: 'white',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
      }
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ paddingTop: '8rem', paddingBottom: '4rem' }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '1.5rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Revolutionize Your Advertising
          </Typography>
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              color: '#94A3B8',
              maxWidth: '800px',
              margin: '0 auto',
              marginBottom: '3rem',
            }}
          >
            Harness the power of AI to deliver personalized, context-aware advertisements that drive real results
          </Typography>
        </motion.div>
      </Container>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  98%
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Ad Engagement Rate
                </Typography>
              </StyledStatCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  2.5x
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Higher Conversion
                </Typography>
              </StyledStatCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  50M+
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  Monthly Impressions
                </Typography>
              </StyledStatCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledStatCard>
                <Typography variant="h2" sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  24/7
                </Typography>
                <Typography variant="h6" sx={{ color: '#94A3B8' }}>
                  AI Optimization
                </Typography>
              </StyledStatCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          style={{ paddingBottom: '6rem' }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">🚀</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Smart Ad Placement
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Our AI analyzes conversation context to place your ads at the perfect moment, ensuring maximum engagement
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">🎯</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Precise Targeting
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Reach your ideal audience with context-aware targeting that adapts in real-time to user behavior
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">📊</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Performance Analytics
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Track engagement and optimize campaigns with detailed insights and real-time analytics
                </Typography>
              </StyledFeatureCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            What Our Clients Say
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledTestimonialCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>JD</Avatar>
                  <Box>
                    <Typography variant="h6">John Doe</Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>Marketing Director</Typography>
                  </Box>
                </Box>
                <Rating value={5} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  "The AI-powered ad placement has transformed our campaign performance. We've seen a 300% increase in engagement since switching to Reasonic."
                </Typography>
              </StyledTestimonialCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledTestimonialCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>AS</Avatar>
                  <Box>
                    <Typography variant="h6">Alice Smith</Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>E-commerce Manager</Typography>
                  </Box>
                </Box>
                <Rating value={5} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  "The contextual targeting is incredible. Our ads now appear at exactly the right moment, leading to higher conversion rates and better ROI."
                </Typography>
              </StyledTestimonialCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledTestimonialCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>MJ</Avatar>
                  <Box>
                    <Typography variant="h6">Michael Johnson</Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>Startup Founder</Typography>
                  </Box>
                </Box>
                <Rating value={5} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  "The analytics dashboard gives us real-time insights that help us optimize our campaigns on the fly. It's a game-changer for our business."
                </Typography>
              </StyledTestimonialCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledPricingCard>
                <Typography variant="h5" sx={{ mb: 2 }}>Starter</Typography>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  $99<span style={{ fontSize: '1rem', color: '#94A3B8' }}>/month</span>
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ 10,000 monthly impressions</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Basic targeting</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Standard analytics</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Email support</Typography>
                </Box>
                <TrackedButton
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: '#3B82F6',
                    color: '#3B82F6',
                    '&:hover': {
                      borderColor: '#2563EB',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                  trackingName="pricing_cta"
                  trackingCategory="pricing"
                  trackingData={{ tier: "starter", price: 99 }}
                >
                  Get Started
                </TrackedButton>
              </StyledPricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledPricingCard
                sx={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                }}
              >
                <Typography variant="h5" sx={{ mb: 2 }}>Professional</Typography>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  $299<span style={{ fontSize: '1rem', color: '#94A3B8' }}>/month</span>
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ 50,000 monthly impressions</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Advanced targeting</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Premium analytics</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Priority support</Typography>
                </Box>
                <TrackedButton
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    },
                  }}
                  trackingName="pricing_cta"
                  trackingCategory="pricing"
                  trackingData={{ tier: "professional", price: 299 }}
                >
                  Get Started
                </TrackedButton>
              </StyledPricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledPricingCard>
                <Typography variant="h5" sx={{ mb: 2 }}>Enterprise</Typography>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  Custom
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Unlimited impressions</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Custom targeting</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Advanced analytics</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>✓ Dedicated support</Typography>
                </Box>
                <TrackedButton
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: '#3B82F6',
                    color: '#3B82F6',
                    '&:hover': {
                      borderColor: '#2563EB',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                  trackingName="pricing_cta"
                  trackingCategory="pricing"
                  trackingData={{ tier: "enterprise", price: "custom" }}
                >
                  Contact Sales
                </TrackedButton>
              </StyledPricingCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 1) 100%)',
        padding: '6rem 0',
        position: 'relative',
        zIndex: 1,
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center' }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                marginBottom: '1.5rem',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Ready to Transform Your Advertising?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#94A3B8',
                marginBottom: '3rem',
              }}
            >
              Join the future of AI-powered advertising today
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <TrackedButton
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  padding: '1rem 2.5rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  },
                }}
                trackingName="create_campaign_cta"
                trackingCategory="advertiser_acquisition"
                trackingData={{ location: "advertisers_page_bottom" }}
              >
                Create Your Campaign
              </TrackedButton>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Advertisers; 